import { useCallback, useContext, useEffect, useRef } from 'react';
import type { FlatListProps } from 'react-native';
import { bridge, isNotNil } from 'shared/src/utils';
import type { GoodsData } from '@banhala/ui-kit';
import type { CodiGoodsItem } from 'shared/src/api';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';
import { GoodsParamsContext } from '../pages/GoodsDetail/context/GoodsParamsContext';

type GoodsItem = GoodsData & CodiGoodsItem;

export const useLogDisplayGoods = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // 찜탭 C군(EXP1768)처럼 route 밖에서 시트로 띄울 때는 GoodsParamsContext 가 주입되어
  // SCREEN 을 대신 채운다. PDP 내 carousel 은 injected 가 없어 GOODS_DETAIL 유지.
  const injected = useContext(GoodsParamsContext);
  const screen = injected?.logging?.screen ?? 'GOODS_DETAIL';

  const goodsItemLoggedList = useRef<Array<number>>([]);
  const pendingItems = useRef<Array<{ index: number; item: GoodsItem }>>([]);

  useEffect(() => {
    if (enabled && pendingItems.current.length > 0) {
      pendingItems.current.forEach(({ index, item }) => {
        if (goodsItemLoggedList.current.includes(index)) return;
        goodsItemLoggedList.current.push(index);

        bridge.logAnalyticsEvent({
          channel: ['amplitude', 'ablylog', 'firebase'],
          name: 'DISPLAY_GOODS',
          ably_only_log: item.logging.ably_only_log,
          analytics: {
            SCREEN: screen,
            ad: item.item.ad,
            ...item.logging.analytics,
            INDEX: index,
          },
        });
        if (isNotNil(item.item.ad)) {
          bridge.logAdEvent({ action: AdAction.DISPLAY, ad: item.item.ad });
        }
      });
      pendingItems.current = [];
    }
  }, [enabled, screen]);

  const logDisplayGoods = useCallback<
    NonNullable<FlatListProps<GoodsItem>['onViewableItemsChanged']>
  >(({ viewableItems }) => {
    viewableItems.forEach(({ index, isViewable, item }) => {
      if (index === null || !isViewable) {
        return;
      }
      if (goodsItemLoggedList.current.includes(index)) {
        return;
      }

      if (!enabledRef.current) {
        if (!pendingItems.current.some(p => p.index === index)) {
          pendingItems.current.push({ index, item });
        }
        return;
      }

      goodsItemLoggedList.current.push(index);

      bridge.logAnalyticsEvent({
        channel: ['amplitude', 'ablylog', 'firebase'],
        name: 'DISPLAY_GOODS',
        ably_only_log: item.logging.ably_only_log,
        analytics: {
          SCREEN: screen,
          ad: item.item.ad,
          ...item.logging.analytics,
          INDEX: index,
        },
      });
      if (isNotNil(item.item.ad)) {
        bridge.logAdEvent({ action: AdAction.DISPLAY, ad: item.item.ad });
      }
    });
  }, [screen]);

  return { logDisplayGoods };
};
